// v2 bakery — an open-air bread-oven yard: a big stone-domed bread oven on a
// masonry plinth under its own tiled canopy on four posts, a tall brick chimney
// rising through the roof, a peel resting on the hearth shelf, a log store, and
// beside it a canopied shop counter of golden loaves facing the road. Keeps the
// signature glowing orange oven mouth. Footprint-native 2x2. Ported 1:1 from
// age-of-mcp's v2/bakery.ts, using the library's shared `foundationSlab`,
// `scaffold`, `barrel` and `industry` (gableRoof, emberMouth) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, barrel, industry, benchWork, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const STONE  = "#8c847a";   // oven plinth + hearth
const DOME   = "#a39686";   // domed oven stone
const BRICK  = "#8a4a38";   // chimney brick
const ROOF   = "#a85a3a";   // terracotta canopy tiles
const RIDGE  = "#7a3f28";
const WOOD   = "#8a5e34";   // posts + counter
const PALE   = "#cdb98e";   // peel blade
const BREAD  = "#c89a52";   // golden loaves
const SACK   = "#d8ccae";   // flour sacks
const LOG    = "#7a5526";

const BASE  = 0.14;         // foundation slab top
const OX    = -0.45;        // oven centre x (west half)
const OZ    = -0.05;        // oven centre z
const PLINTH_TOP = BASE + 0.18;   // 0.32

// The masonry plinth the oven dome sits on, plus the hearth shelf in front.
function plinth(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.78, 0.18, 0.78), color: STONE,
      position: [OX, BASE + 0.09, OZ] },
    { geometry: new THREE.BoxGeometry(0.42, 0.06, 0.20), color: STONE,
      position: [OX, PLINTH_TOP - 0.03, 0.42] },
  ];
}

// The stone bread-oven dome (squashed sphere), full or part-built.
function dome(squash: number): Part {
  return {
    geometry: new THREE.SphereGeometry(0.34, 8, 6), color: DOME,
    position: [OX, PLINTH_TOP + 0.06, OZ], scale: [1, squash, 1],
  };
}

// The tall brick chimney rising behind the dome, `h` tall, with a cap.
function chimney(h: number): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.16, h, 0.16), color: BRICK,
      position: [OX - 0.13, 0.40 + h / 2, OZ - 0.20] },
    { geometry: new THREE.BoxGeometry(0.20, 0.05, 0.20), color: "#6e3a2c",
      position: [OX - 0.13, 0.40 + h + 0.025, OZ - 0.20] },
  ];
}

// The "oven is lit" cue: smoke rising off the finished chimney (its cap tops
// out at 0.40 + 1.05 + 0.05) while the oven-mouth embers pulse.
const plume = smokePlume([OX - 0.13, 1.50, OZ - 0.20], { rise: 0.42 });

// The tiled canopy over the oven: four posts and a gabled tile roof.
function canopy(): Part[] {
  const parts: Part[] = [];
  for (const [px, pz] of [[-0.86, -0.47], [-0.04, -0.47], [-0.86, 0.37], [-0.04, 0.37]] as const) {
    parts.push({
      geometry: new THREE.BoxGeometry(0.06, 0.78, 0.06), color: WOOD,
      position: [px, BASE + 0.39, pz],
    });
  }
  parts.push(...industry.gableRoof(OX, OZ, 0.92, 0.96, 1.04, 0.26, ROOF, RIDGE));
  return parts;
}

// The baker's peel resting flat on the hearth shelf.
function peel(): Part[] {
  const shelfTop = PLINTH_TOP + 0.015;
  return [
    { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.40, 4), color: WOOD,
      position: [-0.48, shelfTop, 0.45], rotation: [0, 0, Math.PI / 2] },
    { geometry: new THREE.BoxGeometry(0.11, 0.015, 0.14), color: PALE,
      position: [-0.70, shelfTop, 0.45] },
  ];
}

// The shop counter on the east half: counter + apron, golden loaves and a
// sloped tile awning on two back posts, all facing the road (+z).
function shop(): Part[] {
  const cx = 0.5;
  const counterTop = BASE + 0.43;
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.70, 0.10, 0.24), color: WOOD, position: [cx, BASE + 0.38, 0.40] },
    { geometry: new THREE.BoxGeometry(0.70, 0.28, 0.05), color: WOOD, position: [cx, BASE + 0.19, 0.50] },
    { geometry: new THREE.BoxGeometry(0.05, 0.66, 0.05), color: WOOD, position: [cx - 0.32, BASE + 0.33, 0.18] },
    { geometry: new THREE.BoxGeometry(0.05, 0.66, 0.05), color: WOOD, position: [cx + 0.32, BASE + 0.33, 0.18] },
    { geometry: new THREE.BoxGeometry(0.78, 0.04, 0.46), color: ROOF,
      position: [cx, 0.748, 0.33], rotation: [0.32, 0, 0] },
  ];
  for (let i = 0; i < 4; i++) {
    parts.push({
      geometry: new THREE.SphereGeometry(0.055, 7, 5), color: BREAD,
      position: [cx - 0.24 + i * 0.16, counterTop + 0.035, 0.40], scale: [1, 0.7, 1.35],
    });
  }
  return parts;
}

// Flour sacks and the oven's log store filling out the yard.
function yard(): Part[] {
  const parts: Part[] = [
    { geometry: new THREE.BoxGeometry(0.18, 0.20, 0.18), color: SACK,
      position: [0.30, BASE + 0.10, -0.52], rotation: [0, 0.25, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.18, 0.16), color: SACK,
      position: [0.34, BASE + 0.28, -0.55], rotation: [0, -0.2, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.19, 0.16), color: SACK,
      position: [0.52, BASE + 0.095, -0.48], rotation: [0, 0.5, 0] },
    barrel(0.80, -0.30),
  ];
  for (const [ly, lz] of [[0.05, 0.52], [0.05, 0.62], [0.135, 0.57]] as const) {
    parts.push({
      geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.30, 6), color: LOG,
      position: [0.02, BASE + ly, lz], rotation: [0, 0, Math.PI / 2],
    });
  }
  return parts;
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(1.94)];
  if (s === 1) return [foundationSlab(1.94), ...plinth(), dome(0.5), ...scaffold(1.5, 0.9)];
  if (s === 2) {
    return [
      foundationSlab(1.94), ...plinth(), dome(0.82), ...chimney(0.6),
      ...industry.emberMouth(OX, 0.42, 0.30, 0.30, 0.24),
      ...scaffold(1.5, 1.35),
    ];
  }
  return [
    foundationSlab(1.94),
    ...plinth(),
    dome(0.82),
    ...industry.emberMouth(OX, 0.42, 0.30, 0.30, 0.24, "embers"),
    ...chimney(1.05),
    ...plume.parts,
    ...canopy(),
    ...peel(),
    ...shop(),
    ...yard(),
  ];
}

export const bakeryV2 = defineModel({
  id: "bakery-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Bakery (v2)",
  tags: ["building","bakery","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly bakery.",
  description: "A baker's oven that bakes flour and firewood into bread.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Oven lit",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [...plume.tracks, firePulse("embers", [OX, 0.42, 0.31])],
    },
  ],
  stations: [
    { id: "bench-a", position: [-0.45, 0.14, 0.62], facing: Math.PI },
    { id: "bench-b", position: [0.5, 0.14, 0.14], facing: 0 },
  ],
  interactions: [
    { id: "bake", label: "At the oven", station: "bench-a", variants: ["complete"], ...benchWork() },
  ],
});
